import {
	resolveFinancialCurrency,
	toApiSortOrder,
} from "@/features/financialShared";
import type {
	GetMonthlyServicePeriodUnitsArgs,
	MonthlyPeriodUnitsHeader,
	MonthlyServicePeriodUnitsResponse,
	UpdateMonthlyUnitStatementBody,
} from "./monthlyStatements.api.types";
import {
	type MonthlyLedgerRow,
	type MonthlyResumeLine,
	type MonthlyStatementOwner,
	MonthlyTransactionItemType,
	type MonthlyUnitDetail,
	type MonthlyUnitListItem,
} from "./monthlyStatements.types";

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ""): string {
	return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
	if (typeof value === "boolean") return value;
	return fallback;
}

function firstNonEmpty(values: unknown[]): string {
	for (const value of values) {
		const text = asString(value).trim();
		if (text) return text;
	}
	return "";
}

export function isPlaceholderPropertyName(name: string | undefined): boolean {
	const trimmed = name?.trim() ?? "";
	return !trimmed || trimmed === "Untitled property";
}

export function resolveMonthlyPropertyName(
	...candidates: Array<string | undefined>
): string {
	for (const candidate of candidates) {
		if (!isPlaceholderPropertyName(candidate)) {
			return candidate?.trim() ?? "";
		}
	}
	return "Untitled property";
}

export function buildMonthlyUnitDetailLabel(
	propertyName: string,
	unitCode: string,
): string {
	return (
		[propertyName, unitCode]
			.filter(
				(part) => part && part !== "—" && !isPlaceholderPropertyName(part),
			)
			.join(" · ") || "—"
	);
}

function unwrapData(raw: unknown): Record<string, unknown> {
	const root = asRecord(raw) ?? {};
	return asRecord(root.data) ?? root;
}

function formatShortDate(value: string): string {
	const trimmed = value.trim();
	if (!trimmed) return "";
	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) return trimmed;
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

const MONTH_INDEX: Record<string, number> = {
	jan: 0,
	feb: 1,
	mar: 2,
	apr: 3,
	may: 4,
	jun: 5,
	jul: 6,
	aug: 7,
	sep: 8,
	oct: 9,
	nov: 10,
	dec: 11,
};

function pad2(value: number): string {
	return String(value).padStart(2, "0");
}

function toIsoDateParts(year: number, monthIndex: number, day: number): string {
	if (
		!Number.isFinite(year) ||
		!Number.isFinite(monthIndex) ||
		!Number.isFinite(day) ||
		year < 1000 ||
		monthIndex < 0 ||
		monthIndex > 11 ||
		day < 1 ||
		day > 31
	) {
		return "";
	}
	return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

export function toIsoDateString(raw: string): string {
	const trimmed = raw.trim();
	if (!trimmed || trimmed === "—" || trimmed.toLowerCase() === "unknown") {
		return "";
	}
	const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (isoMatch) {
		return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
	}
	const dmyMatch = trimmed.match(
		/^(\d{1,2})[-/ ]([A-Za-z]{3,9})[-/, ]+(\d{2,4})$/,
	);
	if (dmyMatch) {
		const day = Number(dmyMatch[1]);
		const monthIndex = MONTH_INDEX[dmyMatch[2].slice(0, 3).toLowerCase()];
		let year = Number(dmyMatch[3]);
		if (year < 100) year += year >= 70 ? 1900 : 2000;
		if (monthIndex != null) return toIsoDateParts(year, monthIndex, day);
	}
	const mdyMatch = trimmed.match(/^([A-Za-z]{3,9})\s+(\d{1,2}),?\s+(\d{2,4})$/);
	if (mdyMatch) {
		const monthIndex = MONTH_INDEX[mdyMatch[1].slice(0, 3).toLowerCase()];
		const day = Number(mdyMatch[2]);
		let year = Number(mdyMatch[3]);
		if (year < 100) year += year >= 70 ? 1900 : 2000;
		if (monthIndex != null) return toIsoDateParts(year, monthIndex, day);
	}
	const parsed = new Date(trimmed);
	if (Number.isNaN(parsed.getTime())) return "";
	return toIsoDateParts(
		parsed.getFullYear(),
		parsed.getMonth(),
		parsed.getDate(),
	);
}

function toDateInputValue(raw: string): string {
	return toIsoDateString(raw);
}

function parseNamedPerson(raw: unknown): string {
	if (typeof raw === "string") return raw.trim();
	const row = asRecord(raw);
	if (!row) return "";
	return asString(row.name ?? row.fullName).trim();
}

function initialsFromName(name: string): string {
	return name
		.replace(/\b(mr|ms|mrs|dr)\b\.?/gi, "")
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((part) => part[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);
}

function parseOwner(raw: unknown): MonthlyStatementOwner | null {
	const row = asRecord(raw);
	if (!row) return null;
	const name = asString(row.name ?? row.fullName).trim();
	if (!name) return null;
	const email = asString(row.email).trim();
	const phone = asString(row.phone).trim();
	return {
		name,
		email,
		phone,
		initials: asString(row.initials).trim() || initialsFromName(name) || "—",
		unitNumber: asString(
			row.unitNumber ?? row.propertyUnitNumber ?? row.unit,
		).trim(),
	};
}

function mapItemTypeFromApi(
	raw: unknown,
): (typeof MonthlyTransactionItemType)[keyof typeof MonthlyTransactionItemType] {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase();
	if (value === "CREDIT" || value === "PAYMENT") {
		return MonthlyTransactionItemType.CREDIT;
	}
	return MonthlyTransactionItemType.DEBIT;
}

function mapItemTypeToApi(type: string): "DEBIT" | "CREDIT" {
	const value = type.trim().toUpperCase();
	if (value === "CREDIT" || value === "PAYMENT") return "CREDIT";
	return "DEBIT";
}

function roundMoney(value: number): number {
	return Math.round(value * 100) / 100;
}

function isResumeSummaryLine(line: MonthlyResumeLine): boolean {
	if (line.tone === "total" || line.tone === "paid" || line.tone === "balance") {
		return true;
	}
	const id = line.id.trim().toLowerCase();
	if (id === "totaldue" || id === "totalpaid" || id === "balance") return true;
	const label = line.label.trim().toLowerCase();
	return (
		label === "total due" ||
		label === "total paid" ||
		label === "balance" ||
		label === "outstanding balance"
	);
}

export function editableMonthlyResumeRows(
	rows: MonthlyResumeLine[],
): MonthlyResumeLine[] {
	return rows.filter((row) => !isResumeSummaryLine(row));
}

function normalizeLedgerRow(row: MonthlyLedgerRow): MonthlyLedgerRow {
	if (row.itemType === MonthlyTransactionItemType.CREDIT) {
		return {
			...row,
			debit: null,
			credit: row.credit ?? 0,
		};
	}
	return {
		...row,
		debit: row.debit ?? 0,
		credit: null,
	};
}

export function recalculateMonthlyLedgerBalances(
	rows: MonthlyLedgerRow[],
): MonthlyLedgerRow[] {
	let running = 0;
	return rows.map((row) => {
		const next = normalizeLedgerRow(row);
		running = roundMoney(running + (next.debit ?? 0) - (next.credit ?? 0));
		return { ...next, balance: running };
	});
}

function buildResumeLines(
	rows: MonthlyResumeLine[],
	totalDue: number,
	totalPaid: number,
	balance: number,
	totalDueLabel = "Total Due",
	totalPaidLabel = "Total Paid",
): MonthlyResumeLine[] {
	return [
		...rows,
		{ id: "totalDue", label: totalDueLabel, amount: totalDue, tone: "total" },
		{ id: "totalPaid", label: totalPaidLabel, amount: totalPaid, tone: "paid" },
		{ id: "balance", label: "Outstanding Balance", amount: balance, tone: "balance" },
	];
}

function parseHeader(
	raw: unknown,
	fallback: { propertyId: string; periodId: string },
): MonthlyPeriodUnitsHeader {
	const row = asRecord(raw) ?? {};
	return {
		propertyId: asString(row.propertyId, fallback.propertyId),
		propertyName: asString(row.propertyName).trim() || "Untitled property",
		location: asString(row.location).trim(),
		periodId: asString(row.periodId, fallback.periodId),
		periodLabel:
			asString(row.periodLabel).trim() ||
			[asString(row.periodStart), asString(row.periodEnd)]
				.filter(Boolean)
				.join(" – ") ||
			"Statement period",
		periodStart: asString(row.periodStart),
		periodEnd: asString(row.periodEnd),
		balanceAsOfDate: asString(row.balanceAsOfDate),
		uploadedDate: asString(row.uploadedDate),
		uploadedBy: parseNamedPerson(row.uploadedBy),
		canUpload: asBoolean(row.canUpload, true),
		canEdit: asBoolean(row.canEdit, true),
	};
}

function parseUnitListItem(raw: unknown): MonthlyUnitListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.id ?? row.statementId).trim();
	if (!id) return null;
	const owner = asRecord(row.owner);
	const rawBalance = row.balance;
	const hasBalance = rawBalance != null && rawBalance !== "";
	return {
		id,
		unitCode:
			asString(row.propertyUnitNumber ?? row.unitCode ?? row.unit).trim() ||
			"—",
		ownerName:
			parseNamedPerson(row.owner) || asString(row.ownerName).trim() || "—",
		ownerEmail: asString(owner?.email ?? row.ownerEmail).trim(),
		reportId: asString(row.reportId).trim() || "—",
		debit: asNumber(row.totalDebit ?? row.debit),
		credit: asNumber(row.totalCredit ?? row.credit),
		balance: hasBalance ? asNumber(rawBalance) : null,
		balanceDisplay: asString(row.balanceDisplay).trim() || undefined,
		balanceDueDate:
			formatShortDate(asString(row.balanceDueDate)) ||
			asString(row.balanceDueDate).trim() ||
			"—",
		currency: resolveFinancialCurrency(row.currency),
		canView: asBoolean(row.canView, true),
		canEdit: asBoolean(row.canEdit, true),
	};
}

export function parseMonthlyServicePeriodUnitsResponse(
	raw: unknown,
	fallback: { propertyId: string; periodId: string },
): MonthlyServicePeriodUnitsResponse {
	const data = unwrapData(raw);
	const items = Array.isArray(data.items)
		? data.items
				.map(parseUnitListItem)
				.filter((item): item is MonthlyUnitListItem => Boolean(item))
		: [];
	const stats = asRecord(data.stats) ?? {};
	return {
		header: parseHeader(data.header, fallback),
		stats: {
			totalUnits: asNumber(stats.totalUnits, items.length),
			settled: asNumber(stats.settled),
			outstanding: asNumber(stats.outstanding),
			pastDue: asNumber(stats.pastDue),
			credit: asNumber(stats.credit),
		},
		items,
		page: Math.max(1, asNumber(data.page, 1)),
		pageSize: Math.max(1, asNumber(data.pageSize, 10)),
		total: asNumber(data.total, items.length),
		totalPages: Math.max(1, asNumber(data.totalPages, 1)),
		emptyMessage: asString(data.emptyMessage).trim() || "No units found.",
	};
}

export function buildMonthlyServicePeriodUnitsParams(
	args: GetMonthlyServicePeriodUnitsArgs,
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
		sortBy: args.sortBy?.trim() || "UNIT",
		sortOrder: toApiSortOrder(args.sortOrder),
	};
	if (args.search?.trim()) params.search = args.search.trim();
	return params;
}

function parseResumeRows(raw: unknown): MonthlyResumeLine[] {
	if (!Array.isArray(raw)) return [];
	return raw.map((item, index) => {
		const row = asRecord(item) ?? {};
		return {
			id: asString(row.id).trim() || `resume-${index}`,
			label: asString(row.label).trim() || "Line",
			amount: asNumber(row.amount),
		};
	});
}

function parseLedgerRows(raw: unknown): MonthlyLedgerRow[] {
	if (!Array.isArray(raw)) return [];
	return raw.map((item, index) => {
		const row = asRecord(item) ?? {};
		const dateRaw = asString(row.date);
		const dateIso = toIsoDateString(dateRaw);
		const debit =
			row.debit == null || row.debit === "" ? null : asNumber(row.debit);
		const credit =
			row.credit == null || row.credit === "" ? null : asNumber(row.credit);
		return {
			id: asString(row.id).trim() || `ledger-${index}`,
			date: formatShortDate(dateRaw) || dateRaw || "—",
			dateIso,
			invoiceNumber: asString(row.invoiceNumber).trim(),
			code: asNumber(row.code),
			concept: asString(row.concept).trim(),
			itemType: mapItemTypeFromApi(row.itemType),
			debit,
			credit,
			balance: asNumber(row.balance),
			notes: asString(row.notes),
		};
	});
}

export function parseMonthlyUnitStatement(
	raw: unknown,
	fallback: { propertyId: string; periodId: string; statementId: string },
): MonthlyUnitDetail {
	const data = unwrapData(raw);
	const meta = asRecord(data.meta) ?? {};
	const resume = asRecord(data.resume) ?? {};
	const ledger = asRecord(data.ledger) ?? {};
	const owners = Array.isArray(data.owners)
		? data.owners
				.map(parseOwner)
				.filter((owner): owner is MonthlyStatementOwner => Boolean(owner))
		: [];
	const primaryOwner = owners[0];
	const ownerName =
		primaryOwner?.name || asString(data.ownerName).trim() || "—";
	const reportId = asString(meta.reportId).trim() || "—";
	const header = asRecord(data.header);
	const propertyRecord = asRecord(data.property);
	const unitRecord = asRecord(data.unit);
	const unitCodeCandidate = firstNonEmpty([
		data.propertyUnitNumber,
		data.unitNumber,
		unitRecord?.number,
		unitRecord?.unitNumber,
		unitRecord?.code,
		meta.propertyUnitNumber,
		meta.unitNumber,
		meta.unit,
		header?.propertyUnitNumber,
		header?.unitNumber,
		primaryOwner?.unitNumber,
	]);
	const unitCodeFromField = asString(
		typeof data.unit === "string" ? data.unit : data.unitCode,
	).trim();
	const reportIdTail = reportId.split(/[-_/]/).pop()?.trim() ?? "";
	const unitCodeFromReport =
		reportIdTail &&
		reportIdTail !== reportId &&
		/^\d+[A-Za-z]?$/.test(reportIdTail)
			? reportIdTail
			: "";
	const unitCode =
		unitCodeCandidate ||
		(unitCodeFromField && unitCodeFromField !== reportId
			? unitCodeFromField
			: "") ||
		unitCodeFromReport ||
		"—";
	const propertyName = firstNonEmpty([
		data.propertyName,
		header?.propertyName,
		meta.propertyName,
		propertyRecord?.propertyName,
		propertyRecord?.name,
	]);
	const periodLabel =
		asString(meta.statementPeriod).trim() ||
		asString(header?.periodLabel).trim() ||
		"Statement period";
	const resumeRows = editableMonthlyResumeRows(parseResumeRows(resume.rows));
	const totalDue = asNumber(resume.totalDue);
	const totalPaid = asNumber(resume.totalPaid);
	const totalDueLabel = asString(resume.totalDueLabel).trim() || "Total Due";
	const totalPaidLabel = asString(resume.totalPaidLabel).trim() || "Total Paid";
	const finalBalance = asNumber(resume.balance ?? ledger.finalBalance);
	const balanceDueDateRaw = asString(meta.balanceDueDate);
	const statementDateRaw = asString(meta.statementDate);
	const ledgerRows = parseLedgerRows(ledger.rows);

	return {
		id: asString(data.statementId, fallback.statementId),
		statementId: asString(data.statementId, fallback.statementId),
		propertyId: fallback.propertyId,
		periodId: fallback.periodId,
		propertyName: propertyName || "Untitled property",
		reportId,
		unitCode,
		ownerName,
		ownerInitials: primaryOwner?.initials || initialsFromName(ownerName) || "—",
		ownerPhone: primaryOwner?.phone || asString(data.ownerPhone),
		ownerEmail: primaryOwner?.email || asString(data.ownerEmail),
		unitDetail: buildMonthlyUnitDetailLabel(
			propertyName || "Untitled property",
			unitCode,
		),
		owners,
		servicePeriodLabel: periodLabel,
		statementDate: formatShortDate(statementDateRaw) || statementDateRaw || "—",
		balanceDueDate:
			formatShortDate(balanceDueDateRaw) || balanceDueDateRaw || "—",
		balanceDueDateIso: toDateInputValue(balanceDueDateRaw),
		currency: resolveFinancialCurrency(meta.currency),
		resumeRows,
		resumeLines: buildResumeLines(
			resumeRows,
			totalDue,
			totalPaid,
			finalBalance,
			totalDueLabel,
			totalPaidLabel,
		),
		totalDue,
		totalPaid,
		totalDueLabel,
		totalPaidLabel,
		ledgerRows,
		finalBalance,
		finalBalanceDisplay: asString(
			ledger.finalBalanceDisplay || resume.balanceDisplay,
		).trim(),
		transactionCount: asNumber(ledger.transactionCount, ledgerRows.length),
		canEdit: asBoolean(data.canEdit, true),
		canDownloadCsv: asBoolean(data.canDownloadCsv, true),
		canDownloadPdf: asBoolean(data.canDownloadPdf, true),
	};
}

export function cloneMonthlyUnitDetail(
	detail: MonthlyUnitDetail,
): MonthlyUnitDetail {
	return structuredClone(detail);
}

export function rebuildMonthlyResumeLines(
	detail: MonthlyUnitDetail,
): MonthlyUnitDetail {
	const resumeRows = editableMonthlyResumeRows(detail.resumeRows);
	const totalDue = roundMoney(
		resumeRows.reduce((sum, row) => sum + row.amount, 0),
	);
	const totalPaid = roundMoney(detail.totalPaid);
	const outstanding = roundMoney(totalDue - totalPaid);
	const ledgerRows = recalculateMonthlyLedgerBalances(detail.ledgerRows);
	return {
		...detail,
		resumeRows,
		totalDue,
		totalPaid,
		ledgerRows,
		transactionCount: ledgerRows.length,
		finalBalance: outstanding,
		finalBalanceDisplay: "",
		resumeLines: buildResumeLines(
			resumeRows,
			totalDue,
			totalPaid,
			outstanding,
			detail.totalDueLabel,
			detail.totalPaidLabel,
		),
	};
}

export function buildUpdateMonthlyUnitStatementBody(
	detail: MonthlyUnitDetail,
): UpdateMonthlyUnitStatementBody {
	return {
		balanceDueDate: toIsoDateString(
			detail.balanceDueDateIso || detail.balanceDueDate,
		),
		totalDue: detail.totalDue,
		totalPaid: detail.totalPaid,
		resumeRows: detail.resumeRows.map((row) => ({
			label: row.label,
			amount: row.amount,
		})),
		ledgerRows: detail.ledgerRows.map((row) => ({
			id: row.id,
			date: toIsoDateString(row.dateIso || row.date),
			invoiceNumber: row.invoiceNumber,
			code: Number.isFinite(row.code) ? row.code : 0,
			concept: row.concept,
			itemType: mapItemTypeToApi(row.itemType),
			debit: row.debit ?? 0,
			credit: row.credit ?? 0,
			balance: row.balance,
			notes: row.notes,
		})),
	};
}
